import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  type CreateTableauDataSourceRequest,
  CreateTableauDataSourceRequestSchema,
} from '@innermate/shared';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

interface TableauFormProps {
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (body: CreateTableauDataSourceRequest) => void;
  onCancel?: () => void;
}

type AuthKind = 'pat' | 'basic';
type TestStatus = 'idle' | 'testing' | 'ok' | 'fail';

interface FormState {
  name: string;
  serverUrl: string;
  siteContentUrl: string;
  authKind: AuthKind;
  user: string;
  secret: string;
  description: string;
}

const INITIAL: FormState = {
  name: '',
  serverUrl: 'https://',
  siteContentUrl: '',
  authKind: 'pat',
  user: '',
  secret: '',
  description: '',
};

export function TableauForm({ submitLabel, submitting, onSubmit, onCancel }: TableauFormProps) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (testStatus !== 'idle') {
      setTestStatus('idle');
      setTestMessage(null);
    }
  };

  const buildPayload = (): CreateTableauDataSourceRequest | null => {
    const auth =
      form.authKind === 'pat'
        ? ({ kind: 'pat', tokenName: form.user, tokenValue: form.secret } as const)
        : ({ kind: 'basic', username: form.user, password: form.secret } as const);
    const draft: CreateTableauDataSourceRequest = {
      name: form.name,
      serverUrl: form.serverUrl,
      siteContentUrl: form.siteContentUrl,
      auth,
      description: form.description.length === 0 ? null : form.description,
    };
    const parsed = CreateTableauDataSourceRequestSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = String(issue.path[0] ?? '_');
        if (!next[k]) next[k] = issue.message;
      }
      setErrors(next);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const onTest = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setTestStatus('testing');
    setTestMessage(null);
    try {
      const res = await api.tableauTestConnection({
        serverUrl: payload.serverUrl,
        siteContentUrl: payload.siteContentUrl,
        auth: payload.auth,
      });
      if (res.ok) {
        setTestStatus('ok');
        setTestMessage(
          `연결 성공${res.serverVersion ? ` — Tableau ${res.serverVersion}` : ''} (${res.latencyMs}ms)`,
        );
      } else {
        setTestStatus('fail');
        setTestMessage(res.error ?? '알 수 없는 오류');
      }
    } catch (err) {
      setTestStatus('fail');
      setTestMessage(err instanceof ApiError ? err.message : (err as Error).message);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (testStatus !== 'ok') return;
    const payload = buildPayload();
    if (!payload) return;
    onSubmit(payload);
  };

  const submitDisabled = submitting || testStatus !== 'ok';

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field label="이름" htmlFor="t-name" error={errors.name} hint="식별용. 공백 가능.">
        <Input
          id="t-name"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="예: 본사 Tableau"
        />
      </Field>

      <Field
        label="서버 URL"
        htmlFor="t-url"
        error={errors.serverUrl}
        hint="https:// 로 시작. 예: https://tableau.company.com"
      >
        <Input
          id="t-url"
          value={form.serverUrl}
          onChange={(e) => update('serverUrl', e.target.value)}
          autoComplete="off"
        />
      </Field>

      <Field
        label="사이트 (선택)"
        htmlFor="t-site"
        error={errors.siteContentUrl}
        hint="Tableau 사이트의 contentUrl. 사이트가 없거나 기본 사이트면 비워두세요."
      >
        <Input
          id="t-site"
          value={form.siteContentUrl}
          onChange={(e) => update('siteContentUrl', e.target.value)}
          placeholder="예: hr (URL 의 /#/site/hr/...) — 비워두면 기본 사이트"
          autoComplete="off"
        />
      </Field>

      <Field label="인증 방식" htmlFor="t-auth" error={errors.auth}>
        <div className="flex gap-2">
          {(['pat', 'basic'] as const).map((k) => (
            <button
              type="button"
              key={k}
              onClick={() => update('authKind', k)}
              className={cn(
                'h-9 px-3 rounded-md border text-xs',
                form.authKind === k
                  ? 'border-accent bg-accent/10 text-foreground'
                  : 'border-border text-foreground-muted hover:text-foreground',
              )}
            >
              {k === 'pat' ? 'PAT (Personal Access Token)' : '사용자 ID/PW'}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label={form.authKind === 'pat' ? 'Token 이름' : '사용자'}
          htmlFor="t-user"
          error={errors.auth}
        >
          <Input
            id="t-user"
            value={form.user}
            onChange={(e) => update('user', e.target.value)}
            autoComplete="off"
            placeholder={form.authKind === 'pat' ? 'innermate-bot' : 'tableau_user'}
          />
        </Field>
        <Field
          label={form.authKind === 'pat' ? 'Token 값' : '비밀번호'}
          htmlFor="t-secret"
          error={errors.auth}
          hint="암호화되어 저장됩니다."
        >
          <PasswordInput
            id="t-secret"
            value={form.secret}
            onChange={(e) => update('secret', e.target.value)}
            autoComplete="new-password"
          />
        </Field>
      </div>

      <Field
        label="설명 (관리자용 메모)"
        htmlFor="t-desc"
        error={errors.description}
        hint="LLM 에 전달되지 않음. 자원별 설명은 등록 후 편집 가능."
      >
        <textarea
          id="t-desc"
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          rows={3}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
          placeholder="이 Tableau 서버의 용도를 적어주세요."
        />
      </Field>

      <div className="rounded-xl border border-border bg-surface-muted/30 p-4">
        <p className="mb-2 text-xs text-foreground-muted">
          연결을 먼저 테스트 한 뒤 저장하세요. 저장하면 자원 (workbook/datasource) 목록이 자동으로
          동기화됩니다.
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onTest}
            disabled={testStatus === 'testing'}
          >
            {testStatus === 'testing' ? <Loader2 className="size-4 animate-spin" /> : null}
            연결 테스트
          </Button>
          {testStatus === 'ok' ? (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="size-3.5" />
              {testMessage}
            </span>
          ) : testStatus === 'fail' ? (
            <span className="inline-flex items-center gap-1 text-xs text-destructive">
              <XCircle className="size-3.5" />
              {testMessage}
            </span>
          ) : testStatus === 'testing' ? (
            <span className="text-xs text-foreground-muted">테스트 중…</span>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        ) : null}
        <Button type="submit" disabled={submitDisabled}>
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
